import { DIAS_SEMANA } from "../local/restaurantProfile";
import { getPromotionClock, isPromotionAvailableAtClock } from "../supabase/edge-functions/_shared/promotionSchedule";
import {
  normalizePromotionPaymentCondition,
  resolvePromotionPaymentPricing,
} from "../supabase/edge-functions/_shared/promotionPayments";
export {
  resolvePromotionPaymentPricing,
  resolveOrderPromotionPayment,
  promotionPaymentNotice,
} from "../supabase/edge-functions/_shared/promotionPayments";

const DEFAULT_DAY_OPEN = "08:00";
const DEFAULT_DAY_CLOSE = "12:00";

const parseCurrencyLikeNumber = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveProductCategoryId = (product) =>
  product?.categoryId ||
  product?.categoria ||
  product?.category_id ||
  "";

const resolveProductSubcategoryId = (product) =>
  product?.subcategoryId ||
  product?.subcategoriaId ||
  product?.subcategory_id ||
  "";

const resolveMenuProductBasePrice = (product) => {
  if (!product) {
    return 0;
  }

  if (product.modoPrecio === "manual") {
    return Number(product.precio || 0);
  }

  if ((product.priceMode || product.modoPrecio) === "suggested") {
    return Number(product.suggestedPrice ?? product.precioSugerido ?? product.price ?? product.precio ?? 0);
  }

  if (product.price !== undefined) {
    return Number(product.price || 0);
  }

  if (product.precioSugerido !== undefined) {
    return Number(product.precioSugerido || 0);
  }

  if (product.precio !== undefined) {
    return Number(product.precio || 0);
  }

  return 0;
};

const resolveItemBaseUnitPrice = (item, product) => {
  if (item?.baseUnitPrice !== undefined) {
    return Number(item.baseUnitPrice || 0);
  }

  if (item?.basePrice !== undefined) {
    return Number(item.basePrice || 0);
  }

  const productPrice = resolveMenuProductBasePrice(product);
  if (productPrice > 0) {
    return productPrice;
  }

  return Number(item?.unitPrice || 0);
};

const resolveComboPromotionPrice = (promotion) => {
  const explicitPrice = Number(promotion?.comboPrice || 0);
  if (explicitPrice > 0) {
    return explicitPrice;
  }

  const summaryPrice = parseCurrencyLikeNumber(promotion?.pricingSummary);
  if (summaryPrice > 0) {
    return summaryPrice;
  }

  const hookPrice = parseCurrencyLikeNumber(promotion?.hook);
  if (hookPrice > 0) {
    return hookPrice;
  }

  return 0;
};

// Component prices copied when a combo is created may be zero or outdated.
// Calculate its fixed-price discount against the current menu, not that copy.
const resolveComboItemUnitPrice = (item, product) =>
  product ? resolveMenuProductBasePrice(product) : Number(item?.unitPrice || 0);

export const normalizePromotionSchedule = (schedule) => {
  const normalized = Array.isArray(schedule) ? schedule : [];

  return DIAS_SEMANA.map((day) => {
    const found = normalized.find((item) => item?.id === day.id);

    return {
      id: day.id,
      label: day.label,
      active: Boolean(found?.active),
      allDay: Boolean(found?.allDay),
      apertura: found?.apertura || DEFAULT_DAY_OPEN,
      cierre: found?.cierre || DEFAULT_DAY_CLOSE,
    };
  });
};

export const buildPromotionScheduleSummary = (schedule) => {
  const normalized = normalizePromotionSchedule(schedule);
  const activeDays = normalized.filter((day) => day.active);

  if (activeDays.length === 0) {
    return "Disponible todos los dias";
  }

  return activeDays
    .map((day) =>
      day.allDay ? `${day.label} todo el dia` : `${day.label} ${day.apertura} a ${day.cierre}`
    )
    .join(" · ");
};

const padPromotionDateSegment = (value) => String(value || 0).padStart(2, "0");

export const buildPromotionLocalDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${padPromotionDateSegment(date.getMonth() + 1)}-${padPromotionDateSegment(
    date.getDate()
  )}`;

export const isDailyMenuPromotion = (promotion) =>
  String(promotion?.comboKind || "").trim().toLowerCase() === "daily_menu";

export const isPromotionAvailableNow = (promotion, nowDate = new Date()) =>
  isPromotionAvailableAtClock(
    { ...promotion, schedule: normalizePromotionSchedule(promotion?.schedule) },
    getPromotionClock(nowDate)
  );

export const isPromotionSelectableNow = (promotion, nowDate = new Date()) =>
  promotion?.active !== false && isPromotionAvailableNow(promotion, nowDate);

export const getPromotionRecordType = (promotion) => {
  if (promotion?.type === "combo") return "combo";
  if (promotion?.type === "promotion") return "promotion";
  return Array.isArray(promotion?.items) && !promotion?.discountType
    ? "combo"
    : "promotion";
};

const getPromotionSpecificity = (promotion) => {
  switch (promotion?.scopeType) {
    case "product":
      return 3;
    case "subcategory":
      return 2;
    case "category":
      return 1;
    default:
      return 0;
  }
};

const matchesPromotionScope = (promotion, product) => {
  if (!promotion || !product) {
    return false;
  }

  const productCategoryId = resolveProductCategoryId(product);
  const productSubcategoryId = resolveProductSubcategoryId(product);

  switch (promotion.scopeType) {
    case "product":
      return promotion.productId === product.id;
    case "subcategory":
      return promotion.subcategoryId === productSubcategoryId;
    case "category":
      return promotion.categoryId === productCategoryId;
    default:
      return promotion.categoryId === productCategoryId;
  }
};

const calculatePromotionPricing = (items = [], promotions = [], menuProducts = []) => {
  const safeItems = Array.isArray(items) ? items : [];
  const activePromotions = (Array.isArray(promotions) ? promotions : []).filter(
    (promotion) => promotion?.active !== false && isPromotionAvailableNow(promotion, new Date())
  );
  const menuProductsById = new Map((menuProducts || []).map((product) => [product.id, product]));

  const listTotal = safeItems.reduce((total, item) => {
    const product = menuProductsById.get(item.productId);
    const quantity = Math.max(1, Number(item?.quantity || 0));
    const displayUnitPrice =
      item?.unitPrice !== undefined
        ? Number(item.unitPrice || 0)
        : resolveItemBaseUnitPrice(item, product);
    return total + quantity * displayUnitPrice;
  }, 0);

  if (!safeItems.length || !activePromotions.length) {
    return {
      listTotal,
      comboDiscountTotal: 0,
      promotionDiscountTotal: 0,
      totalDiscount: 0,
      finalTotal: listTotal,
      adjustments: [],
    };
  }

  const baseProductState = {};

  safeItems.forEach((item) => {
    if (!item?.productId) {
      return;
    }

    const product = menuProductsById.get(item.productId);
    const quantity = Math.max(1, Number(item.quantity || 0));
    const baseUnitPrice = resolveItemBaseUnitPrice(item, product);

    if (!baseProductState[item.productId]) {
      baseProductState[item.productId] = {
        quantity: 0,
        subtotal: 0,
      };
    }

    baseProductState[item.productId].quantity += quantity;
    baseProductState[item.productId].subtotal += baseUnitPrice * quantity;
  });

  const comboAdjustments = [];
  let comboDiscountTotal = 0;

  const comboPromotions = activePromotions
    .filter(
      (promotion) =>
        getPromotionRecordType(promotion) === "combo" &&
        Array.isArray(promotion.items) &&
        promotion.items.length > 0
    )
    .map((promotion) => {
      const bundleBaseTotal = promotion.items.reduce((total, item) => {
        const currentProduct = menuProductsById.get(item.productId);
        const unitPrice = resolveComboItemUnitPrice(item, currentProduct);

        return total + unitPrice * Math.max(1, Number(item.quantity || 0));
      }, 0);

      return {
        promotion,
        bundleDiscount: Math.max(0, bundleBaseTotal - resolveComboPromotionPrice(promotion)),
      };
    })
    .filter((entry) => entry.bundleDiscount > 0)
    .sort((left, right) => right.bundleDiscount - left.bundleDiscount);

  comboPromotions.forEach(({ promotion, bundleDiscount }) => {
    const matchCount = promotion.items.reduce((minCount, item) => {
      const requiredQuantity = Math.max(1, Number(item.quantity || 0));
      const availableQuantity = Number(baseProductState[item.productId]?.quantity || 0);
      return Math.min(minCount, Math.floor(availableQuantity / requiredQuantity));
    }, Number.POSITIVE_INFINITY);

    if (!Number.isFinite(matchCount) || matchCount <= 0) {
      return;
    }

    promotion.items.forEach((item) => {
      const requiredQuantity = Math.max(1, Number(item.quantity || 0));
      const product = menuProductsById.get(item.productId);
      const itemBaseUnitPrice = resolveComboItemUnitPrice(item, product);

      if (baseProductState[item.productId]) {
        baseProductState[item.productId].quantity -= requiredQuantity * matchCount;
        baseProductState[item.productId].subtotal = Math.max(
          0,
          Number(baseProductState[item.productId].subtotal || 0) -
            itemBaseUnitPrice * requiredQuantity * matchCount
        );
      }
    });

    const amount = bundleDiscount * matchCount;
    comboDiscountTotal += amount;
    comboAdjustments.push({
      id: promotion.id,
      type: "combo",
      title: promotion.title,
      paymentCondition: normalizePromotionPaymentCondition(promotion.paymentCondition),
      amount,
      summary: `${promotion.title} x${matchCount}`,
    });
  });

  const promotionAdjustments = [];
  let promotionDiscountTotal = 0;
  const remainingBaseProductState = { ...baseProductState };
  const promotionRules = activePromotions.filter(
    (promotion) => getPromotionRecordType(promotion) === "promotion"
  );

  while (true) {
    let bestCandidate = null;

    promotionRules.forEach((promotion) => {
      const eligibleProductIds = Object.entries(remainingBaseProductState)
        .filter(([, state]) => Number(state?.subtotal || 0) > 0)
        .map(([productId]) => productId)
        .filter((productId) => matchesPromotionScope(promotion, menuProductsById.get(productId)));

      if (!eligibleProductIds.length) {
        return;
      }

      const eligibleSubtotal = eligibleProductIds.reduce(
        (total, productId) =>
          total + Number(remainingBaseProductState[productId]?.subtotal || 0),
        0
      );

      if (eligibleSubtotal <= 0) {
        return;
      }

      const rawDiscount =
        promotion.discountType === "fixed"
          ? Math.min(Number(promotion.discountValue || 0), eligibleSubtotal)
          : eligibleSubtotal * (Number(promotion.discountValue || 0) / 100);

      const discountAmount = Math.max(0, rawDiscount);
      if (discountAmount <= 0) {
        return;
      }

      const candidate = {
        promotion,
        eligibleProductIds,
        discountAmount,
        specificity: getPromotionSpecificity(promotion),
      };

      if (
        !bestCandidate ||
        candidate.discountAmount > bestCandidate.discountAmount ||
        (candidate.discountAmount === bestCandidate.discountAmount &&
          candidate.specificity > bestCandidate.specificity) ||
        (candidate.discountAmount === bestCandidate.discountAmount &&
          candidate.specificity === bestCandidate.specificity &&
          candidate.eligibleProductIds.length < bestCandidate.eligibleProductIds.length)
      ) {
        bestCandidate = candidate;
      }
    });

    if (!bestCandidate) {
      break;
    }

    promotionDiscountTotal += bestCandidate.discountAmount;
    promotionAdjustments.push({
      id: bestCandidate.promotion.id,
      type: "promotion",
      title: bestCandidate.promotion.title,
      paymentCondition: normalizePromotionPaymentCondition(bestCandidate.promotion.paymentCondition),
      amount: bestCandidate.discountAmount,
      summary:
        bestCandidate.promotion.pricingSummary ||
        bestCandidate.promotion.hook ||
        bestCandidate.promotion.title,
    });

    bestCandidate.eligibleProductIds.forEach((productId) => {
      if (remainingBaseProductState[productId]) {
        remainingBaseProductState[productId] = {
          ...remainingBaseProductState[productId],
          quantity: 0,
          subtotal: 0,
        };
      }
    });
  }

  const totalDiscount = comboDiscountTotal + promotionDiscountTotal;
  const finalTotal = Math.max(0, listTotal - totalDiscount);

  return {
    listTotal,
    comboDiscountTotal,
    promotionDiscountTotal,
    totalDiscount,
    finalTotal,
    adjustments: [...comboAdjustments, ...promotionAdjustments],
  };
};

export const buildPromotionPricing = (items = [], promotions = [], menuProducts = [], paymentMethod = "cash") => {
  const cash = calculatePromotionPricing(items, promotions, menuProducts);
  const hasCashOffer = cash.adjustments.some((item) => item.paymentCondition === "cash_only");
  if (!hasCashOffer) return cash;
  const other = calculatePromotionPricing(
    items,
    promotions.filter((item) => normalizePromotionPaymentCondition(item.paymentCondition) !== "cash_only"),
    menuProducts
  );
  return resolvePromotionPaymentPricing({
    ...cash,
    paymentPrices: { cash, other },
  }, paymentMethod);
};
